import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Layout } from "../../components/Layout";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { useLang } from "../../contexts/LangContext";
import { billingService } from "../../services/billingService";
import { CheckCircle2, Clock, XCircle, ArrowLeft } from "lucide-react";

/** Landing for users coming back from MercadoPago Checkout Pro.
 *  The actual payment status is reflected via webhook → DB; this page
 *  polls /billing/me a few times to give the user friendly feedback. */
export default function PaymentReturn() {
  const { lang } = useLang();
  // PT-BR users see Spanish UI for now (~89% lexical overlap).
  const isEs = lang !== "en";
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [polls, setPolls] = useState(0);

  const status = params.get("status") || "pending"; // MP forwards approved/pending/failure
  const type = params.get("type") || "subscription";

  // Poll /billing/me up to 5 times (1s apart) — gives the webhook time to land.
  useEffect(() => {
    let mounted = true;
    const interval = setInterval(async () => {
      try {
        const data = await billingService.me();
        if (mounted) setMe(data);
      } catch (_e) { /* ignore — webhook may not have arrived yet */ }
      setPolls((p) => p + 1);
    }, 1500);
    return () => { mounted = false; clearInterval(interval); };
  }, []);

  useEffect(() => { if (polls >= 5) { /* stop polling visually */ } }, [polls]);

  const ICON = status === "approved" ? CheckCircle2 : status === "failure" ? XCircle : Clock;
  const COLOR = status === "approved" ? "text-emerald-600" : status === "failure" ? "text-red-600" : "text-amber-600";
  const HEADLINE = {
    approved: { es: "¡Pago aprobado!", en: "Payment approved!" },
    pending: { es: "Pago pendiente", en: "Payment pending" },
    failure: { es: "Pago rechazado", en: "Payment failed" },
  }[status] || { es: "Procesando…", en: "Processing…" };
  const SUB = {
    approved: {
      subscription: { es: "Tu suscripción está activa. Disfruta Lumfi Pro.", en: "Your subscription is now active. Enjoy Lumfi Pro." },
      pack: { es: "Tu pack se acreditó en tu cuenta.", en: "Your pack has been added to your account." },
    },
    pending: {
      subscription: { es: "Tu suscripción aún se está confirmando. Volvemos en unos segundos.", en: "Your subscription is still being confirmed. We'll update in a few seconds." },
      pack: { es: "Tu pago está pendiente — recibirás el pack en cuanto se apruebe.", en: "Payment pending — your pack will be added once approved." },
    },
    failure: {
      subscription: { es: "El pago no se pudo completar. Intenta otra tarjeta o medio de pago.", en: "Payment couldn't be completed. Try a different card or method." },
      pack: { es: "El pago no se pudo procesar.", en: "Payment couldn't be processed." },
    },
  }[status]?.[type] || { es: "", en: "" };

  return (
    <Layout>
      <div className="max-w-lg mx-auto fade-up" data-testid="payment-return">
        <Card className="p-8 rounded-2xl border-slate-200 text-center space-y-4">
          <div className={`mx-auto h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center ${COLOR}`}>
            <ICON className="h-8 w-8" />
          </div>
          <h1 className="font-serif-display text-3xl font-medium text-slate-900 leading-tight" data-testid="payment-headline">
            {isEs ? HEADLINE.es : HEADLINE.en}
          </h1>
          <p className="text-sm text-slate-600">
            {isEs ? SUB.es : SUB.en}
          </p>

          {/* Show current billing snapshot once the webhook has updated it */}
          {me && status === "approved" && (
            <div className="bg-slate-50 rounded-xl p-3 text-left space-y-1.5 text-sm">
              {me.plan && (
                <div className="flex justify-between">
                  <span className="text-slate-500">{isEs ? "Plan activo" : "Active plan"}</span>
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="return-plan-name">{me.plan.name}</Badge>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-500">{isEs ? "Saldo de packs" : "Pack balance"}</span>
                <span className="font-semibold tabular-nums" data-testid="return-ai-balance">{me.ai_credits?.balance ?? 0}</span>
              </div>
            </div>
          )}

          {status === "pending" && (
            <p className="text-xs text-slate-400">
              {isEs ? `Esperando confirmación (${polls}/5)…` : `Awaiting confirmation (${polls}/5)…`}
            </p>
          )}

          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:justify-center pt-2">
            <Button variant="outline" onClick={() => navigate("/pricing")} data-testid="return-back-pricing" className="h-12 sm:h-10">
              <ArrowLeft className="h-4 w-4 mr-1" />
              {isEs ? "Volver a planes" : "Back to plans"}
            </Button>
            <Button variant="outline" onClick={() => navigate("/my-plan")} data-testid="return-go-my-plan" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-12 sm:h-10">
              {isEs ? "Ver mi plan" : "View my plan"}
            </Button>
            <Button onClick={() => navigate("/dashboard")} data-testid="return-go-dashboard" className="h-12 sm:h-10">
              {isEs ? "Ir al dashboard" : "Go to dashboard"}
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
